import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useQueryClient } from "@tanstack/react-query";

interface MemberAdminToggleProps {
  /** All syra_members rows for this person (one per group). */
  entries: Array<{ id: string; group_id: string | null; group_name?: string | null; is_group_admin?: boolean }>;
  memberName: string;
}

/**
 * Toggle to mark/unmark a member as group admin across all their group entries.
 * Requires `admin` or `owner` role on the org.
 */
export function MemberAdminToggle({ entries, memberName }: MemberAdminToggleProps) {
  const { role, isLoading: roleLoading } = useOrgRole();
  const queryClient = useQueryClient();
  const canEdit = role === "admin" || role === "owner";

  const initialAllAdmin = entries.length > 0 && entries.every((e) => e.is_group_admin === true);
  const [isAdmin, setIsAdmin] = useState<boolean>(initialAllAdmin);
  const [saving, setSaving] = useState(false);

  const adminGroupCount = entries.filter((e) => e.is_group_admin === true).length;
  const totalGroups = entries.length;

  const handleToggle = async (next: boolean) => {
    if (!canEdit || saving) return;
    setSaving(true);
    const previous = isAdmin;
    setIsAdmin(next);
    try {
      const ids = entries.map((e) => e.id);
      const { error } = await supabase
        .from("syra_members")
        .update({ is_group_admin: next })
        .in("id", ids);
      if (error) throw error;
      toast.success(
        next
          ? `${memberName} agora é administrador (${ids.length} ${ids.length === 1 ? "grupo" : "grupos"})`
          : `Permissão de admin removida de ${memberName}`,
      );
      queryClient.invalidateQueries({ queryKey: ["members"] });
    } catch (err: any) {
      setIsAdmin(previous);
      toast.error("Erro ao atualizar admin: " + (err?.message ?? "desconhecido"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isAdmin ? (
            <ShieldCheck className="w-4 h-4 text-success" />
          ) : (
            <ShieldOff className="w-4 h-4 text-muted-foreground" />
          )}
          <Label htmlFor="member-admin-toggle" className="text-sm font-medium cursor-pointer">
            Administrador do grupo
          </Label>
        </div>
        <Switch
          id="member-admin-toggle"
          checked={isAdmin}
          onCheckedChange={handleToggle}
          disabled={!canEdit || saving || roleLoading}
        />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {adminGroupCount > 0 && adminGroupCount < totalGroups && (
          <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
            Admin em {adminGroupCount} de {totalGroups} grupos
          </Badge>
        )}
        {!canEdit && (
          <p className="text-xs text-muted-foreground">
            Apenas administradores da organização podem alterar esta permissão.
          </p>
        )}
        {canEdit && (
          <p className="text-xs text-muted-foreground">
            Aplica-se a {totalGroups} {totalGroups === 1 ? "grupo" : "grupos"} deste membro.
          </p>
        )}
      </div>
    </div>
  );
}
